import { FilterType, FilterConfigItem } from "./types";

/**
 * Checks if a filter change is an "apply" or "remove" action.
 */
export const checkIsApplying = (
  type: FilterType,
  filterValue: any,
  currentValues: any,
  checkedArg?: boolean,
) => {
  if (type === FilterType.RANGE) {
    return filterValue?.min !== undefined || filterValue?.max !== undefined;
  }
  if (Array.isArray(currentValues)) {
    return !currentValues.includes(String(filterValue));
  }
  if (typeof currentValues === "string") {
    return currentValues !== String(filterValue);
  }
  if (typeof checkedArg === "boolean") {
    return checkedArg;
  }
  return checkedArg !== false;
};

/**
 * Calculates the offset for the active filters count based on the change.
 */
export const getFilterCountOffset = (type: FilterType, isApplying: boolean, currentValues: any) => {
  let hasValue = false;
  if (type === FilterType.RANGE) {
    hasValue = currentValues?.min !== undefined || currentValues?.max !== undefined;
  } else if (Array.isArray(currentValues)) {
    hasValue = currentValues.length > 0;
  } else {
    hasValue = currentValues !== undefined;
  }

  if (isApplying) {
    // For single-select and range, if we already had a value, the count doesn't change (just replaced)
    if (type === FilterType.SINGLE_SELECT || type === FilterType.RANGE) {
      return hasValue ? 0 : 1;
    }
    return 1;
  }

  // Removing: only decrement if we actually had a value
  return hasValue ? -1 : 0;
};

/**
 * Recursively finds a filter option by its value in a list of options.
 */
export const findOptionByValue = (options: any[], value: string | number): any => {
  if (!Array.isArray(options)) return undefined;

  for (const opt of options) {
    if (String(opt.value ?? opt.id) === String(value)) return opt;
    if (opt.children?.length) {
      const found = findOptionByValue(opt.children, value);
      if (found) return found;
    }
  }
  return undefined;
};

export const toPreselectFilterValues = (values?: (string | number | null | undefined)[]) =>
  (values || [])
    .filter((value): value is string | number => value != null && value !== "")
    .map(String)
    .filter(Boolean);

type FilterConfigItemWithPreselect = FilterConfigItem & {
  preselectThreshold?: number;
  preselectdValues?: string[];
};

export const shouldApplyPreselectRules = (configItem?: FilterConfigItem): boolean => {
  if (!configItem || configItem.type === FilterType.RANGE) return false;

  const { preselectThreshold, preselectdValues, options } =
    configItem as FilterConfigItemWithPreselect;
  if (preselectThreshold == null || !toPreselectFilterValues(preselectdValues).length) return false;
  if (!Array.isArray(options) || options.length <= preselectThreshold) return false;

  return true;
};

/** Disable Clear All when the only applied filter is a sole preselected value. */
export const shouldDisableClearAllForPreselect = (
  appliedFilters: Record<string, unknown>,
  config?: FilterConfigItem[],
): boolean => {
  const appliedEntries = Object.entries(appliedFilters);
  if (appliedEntries.length !== 1) return false;

  const [filterId, stateValue] = appliedEntries[0];
  const preselectdValues = config?.find((item) => item.id === filterId)?.preselectdValues;
  if (!preselectdValues?.length) return false;

  const selected = (Array.isArray(stateValue) ? stateValue : [stateValue])
    .map(String)
    .filter(Boolean);

  if (selected.length !== 1) return false;

  const protectedValues = new Set(toPreselectFilterValues(preselectdValues));
  return protectedValues.has(selected[0]);
};

/** Disable pill remove when it is the only applied filter value and matches a preselected value. */
export const isProtectedPreselectPill = (
  pill: { preselectdValues?: string[]; value: string | number },
  appliedFiltersCount: number,
): boolean => {
  if (appliedFiltersCount !== 1) return false;

  const protectedValues = new Set(toPreselectFilterValues(pill.preselectdValues));
  return protectedValues.has(String(pill.value));
};
